import { useState, useEffect, useCallback } from 'react';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useRealtimeSubscription } from './useRealtimeSubscription';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '../services/api';

/**
 * Hook that provides live notification state.
 * Fetches initial unread count and subscribes to realtime INSERTs.
 */
export function useNotifications() {
  const { user, isAuthenticated } = useSupabaseAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch notifications from the API
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const response = await getNotifications();
      const items = response.data?.notifications || response.data || [];
      setNotifications(items);
      setUnreadCount(items.filter(n => !n.read).length);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Realtime: listen for new notifications for this user
  useRealtimeSubscription({
    table: 'notifications',
    event: 'INSERT',
    filter: user ? `user_id=eq.${user.id}` : undefined,
    enabled: isAuthenticated && !!user,
    onInsert: (newNotification) => {
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
    },
  });

  // Realtime: listen for notification updates (e.g. marked as read from another tab)
  useRealtimeSubscription({
    table: 'notifications',
    event: 'UPDATE',
    filter: user ? `user_id=eq.${user.id}` : undefined,
    enabled: isAuthenticated && !!user,
    onUpdate: (updated) => {
      setNotifications(prev =>
        prev.map(n => (n.id === updated.id ? updated : n))
      );
      // Recount unread
      setNotifications(prev => {
        setUnreadCount(prev.filter(n => !n.read).length);
        return prev;
      });
    },
  });

  // Mark a single notification as read
  const markRead = useCallback(async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  }, []);

  // Mark all as read
  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications read:', err);
    }
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markRead,
    markAllRead,
  };
}

export default useNotifications;
